import { Component, Input, OnInit } from '@angular/core'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { filter, first, map, switchMap, tap } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { competitionMatchEventSelectors } from '../../competition-match-event/competition-match-event.selectors'
import { competitionMatchActions } from '../../competition-match/competition-match.actions'
import { CompetitionPlayer } from '../../competition-player/competition-player'
import { competitionPlayerActions } from '../../competition-player/competition-player.actions'
import { competitionPlayerSelectors } from '../../competition-player/competition-player.selectors'
import { CompetitionPool } from '../../competition-pool/competition-pool'
import { competitionPoolActions } from '../../competition-pool/competition-pool.actions'
import { competitionPoolSelectors } from '../../competition-pool/competition-pool.selectors'
import { CompetitionService } from '../../competition.service'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamSelectors } from '../competition-team.selectors'
import moment from 'moment-mini'
import { CompetitionMatchEventEventTypes } from '../../competition-match-event/competition-match-event'
import { ActivatedRoute } from '@angular/router'

@UntilDestroy()
@Component({
  selector: 'top-scorer-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamTopScorerComponent implements OnInit {
  @Input() competitionId: number

  public teamId$: Observable<number>
  public isLoading$: Observable<boolean>
  public topScorers$: Observable<any[]>
  public memberEntities$: Observable<Dictionary<Member>>
  public competitionTeamEntities$: Observable<Dictionary<CompetitionTeam>>
  public competitionPlayerEntities$: Observable<Dictionary<CompetitionPlayer>>
  public competitionPool$: Observable<CompetitionPool>
  public teamIdsOfTheCompetitionPool: number[] = []

  constructor(
    private store: Store<AppState>,
    public competitionService: CompetitionService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.store.dispatch(
      competitionMatchActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            competitionId: this.competitionId,
            'booking.endDate': {
              [CondOperator.LOWER_THAN_EQUALS]: moment().toISOString()
            },
            'competitionMatchEvents.eventType':
              CompetitionMatchEventEventTypes.GOAL
          },
          join: [
            { field: 'team1' },
            { field: 'team2' },
            { field: 'competitionMatchEvents' },
            { field: 'booking' }
          ]
        })
      })
    )

    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)
    this.competitionTeamEntities$ = this.store.select(
      competitionTeamSelectors.selectEntities
    )
    this.competitionPlayerEntities$ = this.store.select(
      competitionPlayerSelectors.selectEntities
    )

    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )

    this.store.dispatch(
      competitionPoolActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            competitionId: this.competitionId
          },
          join: [{ field: 'competitionTeam' }]
        })
      })
    )

    this.competitionPool$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionPoolSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: teamId
        })
      ),
      map(
        (competitionPools) =>
          competitionPools.filter(
            (competitionPool) =>
              competitionPool?.competitionId == this.competitionId
          )[0]
      )
    )

    this.competitionPool$
      .pipe(first((competitionPool) => Boolean(competitionPool)))
      .subscribe((competitionPool) => {
        this.store
          .select(competitionPoolSelectors.selectByFilter, {
            field: 'name',
            value: competitionPool?.name
          })
          .pipe(first((competitionPools) => Boolean(competitionPools)))
          .subscribe((competitionPools) => {
            competitionPools.forEach((competitionPool) => {
              this.teamIdsOfTheCompetitionPool.push(
                competitionPool.competitionTeamId
              )
            })

            this.store.dispatch(
              competitionPlayerActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: {
                    competitionTeamId: {
                      [CondOperator.IN]: this.teamIdsOfTheCompetitionPool
                    }
                  },
                  join: [{ field: 'member' }]
                })
              })
            )
          })
      })

    this.topScorers$ = this.store
      .select(competitionMatchEventSelectors.selectAll)
      .pipe(
        untilDestroyed(this),
        map((matchesEvents) => {
          const matchEventsOfCompetition = matchesEvents.filter(
            (matchEvent) =>
              this.teamIdsOfTheCompetitionPool.includes(
                matchEvent.competitionTeamId
              ) && matchEvent.eventType == CompetitionMatchEventEventTypes.GOAL
          )

          return this.competitionService.getTopScorers(matchEventsOfCompetition)
        })
      )
  }
}
